import { useState, useEffect, useCallback, useRef } from "react";
import { useAppContext } from "@/App";
import pigeonNotifier from "@/lib/pigeonNotifier";
import { Bird, Volume2, VolumeX, Mic, X } from "lucide-react";

export default function PigeonFAB({ role, events, onDictate }) {
  const { session } = useAppContext();
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [spokenEnabled, setSpokenEnabled] = useState(() => localStorage.getItem("pigeon-tts") === "true");
  const [showMenu, setShowMenu] = useState(false);
  const [recentNotifs, setRecentNotifs] = useState([]);
  const prevCountRef = useRef(0);
  const lang = localStorage.getItem("pigeon-lang") || "en";

  // Init notifier language
  useEffect(() => {
    pigeonNotifier.setLang(lang);
    pigeonNotifier.setEnabled(spokenEnabled);
  }, [lang, spokenEnabled]);

  // Process new events
  useEffect(() => {
    if (!events || events.length === 0) return;

    pigeonNotifier.onNotification = (newEvents) => {
      setUnread(prev => prev + newEvents.length);
      setRecentNotifs(prev => [...newEvents.slice(0, 3), ...prev].slice(0, 6));
      setPulse(true);
      setTimeout(() => setPulse(false), 2000);
    };

    // Only process if event count changed
    if (events.length !== prevCountRef.current) {
      const diff = events.length - prevCountRef.current;
      if (diff > 0 && prevCountRef.current > 0) {
        pigeonNotifier.processEvents(events.slice(0, diff));
      }
      prevCountRef.current = events.length;
    }
  }, [events]);

  // Init: mark all existing events as seen on first load
  useEffect(() => {
    if (events && events.length > 0 && prevCountRef.current === 0) {
      events.forEach(ev => {
        const id = ev.id || `${ev.event_type}-${ev.timestamp}`;
        pigeonNotifier.seenIds.add(id);
      });
      prevCountRef.current = events.length;
    }
  }, [events]);

  const toggleSpoken = useCallback(() => {
    const next = !spokenEnabled;
    setSpokenEnabled(next);
    pigeonNotifier.setEnabled(next);
    localStorage.setItem("pigeon-tts", String(next));
    if (next) {
      pigeonNotifier.speakNow(lang === "nl" ? "Meldingen ingeschakeld" : "Notifications enabled");
    }
  }, [spokenEnabled, lang]);

  const handleFabClick = () => {
    if (showMenu) {
      setShowMenu(false);
    } else {
      setShowMenu(true);
      setUnread(0);
    }
  };

  const isMachinist = role === "machinist";

  return (
    <>
      {/* Notification menu */}
      {showMenu && (
        <div className="fixed bottom-[7.5rem] right-5 z-[80] w-72 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden animate-fadeInUp" data-testid="pigeon-menu">
          <div className="p-3 bg-slate-950 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bird className="w-4 h-4 text-lime-400" />
              <span className="text-xs font-black text-white uppercase tracking-wider">Pigeon Alerts</span>
            </div>
            <button onClick={() => setShowMenu(false)} className="text-slate-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* TTS toggle */}
          <div className="p-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Spoken Notifications</span>
            <button
              data-testid="tts-toggle"
              onClick={toggleSpoken}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                spokenEnabled ? "bg-lime-500 text-slate-950" : "bg-slate-100 text-slate-400"
              }`}
            >
              {spokenEnabled ? <><Volume2 className="w-3.5 h-3.5" /> ON</> : <><VolumeX className="w-3.5 h-3.5" /> OFF</>}
            </button>
          </div>

          {/* Dictate button (machinist only) */}
          {isMachinist && onDictate && (
            <button
              data-testid="dictate-btn"
              onClick={() => { setShowMenu(false); onDictate(); }}
              className="w-full p-3 bg-red-50 border-b border-red-200 flex items-center gap-3 hover:bg-red-100 transition-colors"
            >
              <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center">
                <Mic className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <span className="text-xs font-black text-slate-900 block">Dictate Request</span>
                <span className="text-[9px] text-slate-400">Push to speak — Philogic creates the request</span>
              </div>
            </button>
          )}

          {/* Recent notifications */}
          <div className="max-h-48 overflow-y-auto">
            {recentNotifs.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase">No new alerts</p>
              </div>
            ) : (
              recentNotifs.map((ev, i) => (
                <div key={ev.id || i} className="px-3 py-2.5 border-b border-slate-100 last:border-0 flex items-center gap-2.5">
                  <div className="w-2 h-2 bg-lime-500 rounded-full shrink-0" />
                  <div className="min-w-0">
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-wide block truncate">
                      {pigeonNotifier.getSpokenText(ev.event_type)}
                    </span>
                    <span className="text-[9px] text-slate-400">
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* FAB */}
      <button
        data-testid="pigeon-fab"
        onClick={handleFabClick}
        className={`fixed bottom-24 right-5 z-[70] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl transition-all active:scale-90 ${
          pulse ? "bg-lime-500 animate-pulse shadow-lime-500/50" : "bg-slate-950 hover:bg-slate-800 shadow-slate-950/30"
        }`}
      >
        <Bird className={`w-7 h-7 ${pulse ? "text-slate-950" : "text-lime-400"}`} strokeWidth={1.5} />

        {/* Badge */}
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-600 text-white rounded-full text-[9px] font-black flex items-center justify-center animate-bounceIn">
            {unread > 9 ? "9+" : unread}
          </span>
        )}

        {/* TTS indicator */}
        {spokenEnabled && (
          <span className="absolute -bottom-0.5 -left-0.5 w-3 h-3 bg-lime-400 rounded-full border-2 border-white" />
        )}
      </button>
    </>
  );
}
